/**
 * PWA Context - Полное управление PWA функциями
 * Включает: Install prompt, Update detection, Offline mode, Network status
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PWAContext = createContext(null);

export function PWAProvider({ children }) {
  // Install state
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  
  // Update state
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  
  // Network state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState(null);
  
  // App state
  const [isStandalone, setIsStandalone] = useState(false);
  const [platform, setPlatform] = useState('web');

  // Detect platform and standalone mode
  useEffect(() => {
    // Check if running as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone ||
                       document.referrer.includes('android-app://');
    setIsStandalone(standalone);
    setIsInstalled(standalone);

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/.test(userAgent)) {
      setPlatform('android');
    } else if (/windows/.test(userAgent)) {
      setPlatform('windows');
    } else if (/macintosh|mac os x/.test(userAgent)) {
      setPlatform('macos');
    } else {
      setPlatform('web');
    }
  }, []);

  // Handle beforeinstallprompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Handle appinstalled
  useEffect(() => {
    const handler = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setInstallPrompt(null);
    };

    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  // Service Worker registration and updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Only handle SW in production or when explicitly enabled
      const isProduction = process.env.NODE_ENV === 'production';
      
      if (!isProduction) {
        // In development, don't auto-reload on SW changes
        console.log('PWA: Development mode - SW auto-reload disabled');
        return;
      }
      
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);

        // Listen for updates
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          }
        });
      });

      // Handle controller change (update applied) - only reload once.
      // On a FIRST visit there is no controller yet: the service worker
      // installs, skipWaiting()s and clients.claim()s the open page, which
      // fires controllerchange with nothing to update to. Reloading there
      // rebooted the whole SPA — re-downloading ~4MB and re-running every
      // bootstrap API call — which is what made the first dashboard take
      // ~15s and what painted the second, ghost Layout. Only reload when a
      // previous controller was actually replaced by a new version.
      const hadController = Boolean(navigator.serviceWorker.controller);
      let reloading = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadController || reloading) return;
        reloading = true;
        window.location.reload();
      });
    }
  }, []);

  // Network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Get connection type if available
    if ('connection' in navigator) {
      const updateConnectionInfo = () => {
        const conn = navigator.connection;
        setConnectionType({
          effectiveType: conn.effectiveType,
          downlink: conn.downlink,
          rtt: conn.rtt,
          saveData: conn.saveData
        });
      };
      updateConnectionInfo();
      navigator.connection.addEventListener('change', updateConnectionInfo);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Install PWA
  const installPWA = useCallback(async () => {
    if (!installPrompt) return false;

    try {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setInstallPrompt(null);
        setCanInstall(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Install error:', error);
      return false;
    }
  }, [installPrompt]);

  // Apply update
  const applyUpdate = useCallback(() => {
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [registration]);

  // Check for updates manually
  const checkForUpdates = useCallback(async () => {
    if (registration) {
      try {
        await registration.update();
        return true;
      } catch (error) {
        console.error('Update check failed:', error);
        return false;
      }
    }
    return false;
  }, [registration]);

  // Get iOS install instructions
  const getIOSInstallInstructions = useCallback(() => {
    return {
      steps: [
        'Нажмите на иконку "Поделиться" внизу экрана',
        'Прокрутите вниз и выберите "На экран Домой"',
        'Нажмите "Добавить" в правом верхнем углу'
      ],
      icon: 'share'
    };
  }, []);

  const value = {
    // Install
    canInstall,
    isInstalled,
    installPWA,
    
    // Update
    updateAvailable,
    applyUpdate,
    checkForUpdates,
    
    // Network
    isOnline,
    connectionType,
    
    // Platform
    isStandalone,
    platform,
    isIOS: platform === 'ios',
    isAndroid: platform === 'android',
    isMobile: platform === 'ios' || platform === 'android',
    
    // Helpers
    getIOSInstallInstructions
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
}

export function usePWA() {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within PWAProvider');
  }
  return context;
}

export default PWAContext;
